import React from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import css from './FullScreenImageGallery.scss';
import Icon from 'Components/ThematicIcon/ThematicIcon';
import classNames from 'classnames';
import { getImageSize } from 'Helpers/urlImageSize';
import withDirection from 'HOCs/withDirection/withDirection';
import { ESC_KEY, RIGHT_KEY, LEFT_KEY } from 'Constants/keyStrokes';
import Zoomable from 'Components/zoomable/zoomable';
import GalleryThumbnails from 'Components/GalleryThumbnails/GalleryThumbnails';
import {
    ICONS,
    ICON_COLORS,
    RESPONSIVE_THUMBNAILS_SETTINGS
} from 'Constants/items';

const MAX_CAROUSEL_THUMBNAILS = 7;
const LARGE_ICON_SIZE = 35;
const MEDIUM_ICON_SIZE = 30;
const SMALL_ICON_SIZE = 25;

export class FullScreenImageGallery extends React.PureComponent {
    static propTypes = {
        images: PropTypes.array,
        toggleFullScreen: PropTypes.func,
        initialSlide: PropTypes.number,
        children: PropTypes.oneOfType([
            PropTypes.element,
            PropTypes.array,
            PropTypes.string
        ]),
        alt: PropTypes.string,
        onChange: PropTypes.func,
        beforeChange: PropTypes.func,
        imageWrapperClassname: PropTypes.string,
        direction: PropTypes.oneOf(['ltr', 'rtl']).isRequired,
        showThumbnails: PropTypes.bool
    };

    static defaultProps = {
        images: [],
        initialSlide: 0,
        toggleFullScreen: () => { },
        onChange: () => { },
        beforeChange: () => { },
        alt: '',
        imageWrapperClassname: '',
        showThumbnails: false
    };

    constructor(props) {
        super(props);
        this.state = {
            slideIndex: props.initialSlide
        };
    }

    componentDidMount() {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', this.handleKeyDown, true);
        }
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = 'auto';
            document.removeEventListener('keydown', this.handleKeyDown, true);
        }
    }

    next = e => {
        this.slider.slickNext();
        this.stopEvent(e);
    };

    previous = e => {
        this.slider.slickPrev();
        this.stopEvent(e);
    };

    closeImage = e => {
        this.props.toggleFullScreen(this.state.slideIndex);
        this.stopEvent(e);
    };

    handleKeyDown = e => {
        if (e.keyCode === ESC_KEY) {
            this.props.toggleFullScreen();
        }
        if (e.keyCode === LEFT_KEY) {
            this.previous(e);
        }
        if (e.keyCode === RIGHT_KEY) {
            this.next(e);
        }
    };

    stopEvent = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    onChange = newIndex => {
        this.props.onChange(newIndex);
    };

    goTo = e => {
        this.slider.slickGoTo(parseInt(e.target.value, 10));
        this.stopEvent(e);
    };

    handleBeforeChange = (curr, nxt) => {
        this.setState({
            slideIndex: nxt
        });
        this.props.beforeChange(curr, nxt);
    };

    render() {
        let arrows;
        let arrowClass = css.arrow;
        let arrowSize = LARGE_ICON_SIZE;
        let arrowColor = ICON_COLORS.WHITE_ICONS;

        if (this.props.images.length > 1) {
            if (this.props.showThumbnails) {
                arrowClass = css.customArrow;
                arrowSize = SMALL_ICON_SIZE;
                arrowColor = ICON_COLORS.BLACK_TEXT;
            }
            arrows = (
                <div className={ css.arrows } data-aut-id="arrows">
                    <span
                        className={ classNames(arrowClass, css.prev) }
                        onClick={ this.previous }
                        data-aut-id="leftArrow"
                    >
                        <Icon
                            icon={ ICONS.ARROW_LEFT }
                            direction={ this.props.direction }
                            size={ arrowSize }
                            color={ arrowColor }
                        />
                    </span>
                    <span
                        className={ classNames(arrowClass, css.next) }
                        onClick={ this.next }
                        data-aut-id="rightArrow"
                    >
                        <Icon
                            icon={ ICONS.ARROW_RIGHT }
                            direction={ this.props.direction }
                            size={ arrowSize }
                            color={ arrowColor }
                        />
                    </span>
                </div>
            );
        }

        const content = this.props.images.map((image, index) => {
            const url = getImageSize(image.url, 'full');
            const label = image.label || image.name;

            return (
                <Zoomable key={ index }>
                    <div key={ index }>
                        <div className={ css.slides }>
                            <div
                                className={ classNames(
                                    css.imageWrapper,
                                    this.props.imageWrapperClassname
                                ) }
                            >
                                <img
                                    src={ url }
                                    onClick={ this.stopEvent }
                                    alt={ `${this.props.alt} ${index}` }
                                />
                            </div>
                            {label && <div className={ css.imageLabel }>{label}</div>}
                        </div>
                    </div>
                </Zoomable>
            );
        });

        const settings = {
            speed: 500,
            dots: false,
            arrows: false,
            currentSlide: this.props.initialSlide,
            rtl: this.props.direction === 'rtl',
            infinite: true,
            lazyLoad: true,
            slidesToShow: 1,
            slidesToScroll: 1,
            className: classNames(css.fullScreenSlider, {
                [css.customHeight]: this.props.showThumbnails
            }),
            initialSlide: this.props.initialSlide,
            asNavFor: this.thumbsSlider,
            ref: slider => (this.slider = slider),
            beforeChange: (current, next) => this.handleBeforeChange(current, next),
            afterChange: i => this.onChange(i)
        };

        const thumbsSettings = {
            speed: 500,
            dots: false,
            arrows: false,
            infinite: true,
            slidesToShow: MAX_CAROUSEL_THUMBNAILS,
            slidesToScroll: 1,
            className: css.customThumbs,
            responsive: RESPONSIVE_THUMBNAILS_SETTINGS,
            asNavFor: this.slider,
            focusOnSelect: true,
            swipeToSlide: true,
            currentSlide: this.props.initialSlide,
            initialSlide: this.props.initialSlide,
            ref: thumbsSlider => (this.thumbsSlider = thumbsSlider)
        };

        return (
            <section className={ css.imageGallery } data-aut-id="image-gallery">
                <div
                    className={ css.fullScreen }
                    onClick={ this.closeImage }
                    onKeyDown={ this.handleKeyDown }
                    data-aut-id="full-screen"
                >
                    <Slider { ...settings }>
                        {content}
                    </Slider>
                    <div className={ css.customPaging }>
                        {this.props.showThumbnails && (
                            <GalleryThumbnails
                                images={ this.props.images }
                                settings={ thumbsSettings }
                                title={ this.props.alt }
                                slideIndex={ this.state.slideIndex }
                                direction={ this.props.direction }
                                newThumbsVariant={ true }
                                selectedThumbClassName={ css.selectedThumb }
                                thumbClassName={ css.customThumb }
                                thumbsContainerClassName={ css.customThumbsContainer }
                                showArrow={ this.props.images.length > MAX_CAROUSEL_THUMBNAILS }
                                arrowIconSize={ MEDIUM_ICON_SIZE }
                                arrowIconColor={ ICON_COLORS.WHITE_ICONS }
                                handleNext={ this.next }
                                handlePrev={ this.previous }
                                handleItemClick={ this.goTo }
                                handleThumbsContainerClick={ this.handleThumbsContainerClick }
                            />
                        )}
                        <span className={ this.props.showThumbnails ? css.indexing : '' }>
                            {this.state.slideIndex + 1}/{this.props.images.length}
                        </span>
                    </div>
                    {arrows}
                    <span className={ css.close } data-aut-id="cross-btn" onClick={ this.closeImage }>
                        <Icon
                            icon={ ICONS.CROSS }
                            size={ SMALL_ICON_SIZE }
                            color={ ICON_COLORS.WHITE_ICONS }
                        />
                    </span>
                    {this.props.children}
                </div>
            </section>
        );
    }
}

export default withDirection(FullScreenImageGallery);
